/**
 * MED-1349: rules for rows in the cohort event attendance drawer.
 */
import type {
  CohortEventAttendanceStatus,
  CohortEventRosterRole,
} from '../../../../services/api/cohortEvents';

/**
 * "Mark all attended" sets a row only when the row is required and has no
 * status yet. A required row has an email and is not a withdrawn
 * participant or an optional (invited-only) coach, so one click never adds
 * a payable row for a coach who was only invited (QA round 3).
 */
export function isBulkMarkable(row: {
  selected_status: CohortEventAttendanceStatus | null;
  is_required: boolean;
}): boolean {
  return row.is_required && row.selected_status === null;
}

/**
 * MED-1349 QA round 3: a coach listed only through the event invitation.
 * The API sends is_required false for them; the CM may leave them blank.
 */
export function isOptionalCoach(row: {
  role: CohortEventRosterRole;
  is_required: boolean;
  is_withdrawn: boolean;
  email: string | null;
}): boolean {
  return row.role === 'coach' && !row.is_required && !row.is_withdrawn && row.email !== null;
}

/**
 * MED-1349 QA round 2: a click on the selected status clears it (null).
 * A click on any other status selects that status.
 */
export function nextStatus(
  current: CohortEventAttendanceStatus | null,
  clicked: CohortEventAttendanceStatus,
): CohortEventAttendanceStatus | null {
  return current === clicked ? null : clicked;
}

/**
 * MED-1349 QA round 2: footer text when an absence has no reason. Each row
 * is named (name, else email): up to three names, then "and N more" (QA
 * round 3). Null when every absence has a reason.
 */
export function absenceReasonText(
  rows: {
    name: string | null;
    email: string | null;
    selected_status: CohortEventAttendanceStatus | null;
    selected_reason: string | null;
  }[],
): string | null {
  const missing = rows.filter((r) => r.selected_status === 'absent' && r.selected_reason === null);
  if (missing.length === 0) return null;
  const names = missing.map((r) => r.name || r.email);
  if (names.length === 1) return `An absence needs a reason: ${names[0]}`;
  const shown = names.slice(0, 3).join(', ');
  const more = names.length > 3 ? ` and ${names.length - 3} more` : '';
  return `Absences need a reason: ${shown}${more}`;
}

/**
 * MED-1349 QA round 2: the user ids whose status the CM cleared in this
 * drawer. A row counts only when it loaded with a status and has none now.
 * The API removes only these saved rows, so a row that another CM saved after
 * this drawer loaded is never removed by this save.
 */
export function clearedUserIds(
  rows: {
    user_id: string;
    loaded_status: CohortEventAttendanceStatus | null;
    selected_status: CohortEventAttendanceStatus | null;
  }[],
): string[] {
  return rows
    .filter((r) => r.loaded_status !== null && r.selected_status === null)
    .map((r) => r.user_id);
}
